import { Alert, Button, Card, CardBody, CardHeader, Field, Input, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  invoiceKeys,
  invoiceSettingsQuery,
  updateInvoiceSettings,
} from '../../shared/api/invoicing-queries.js';

/**
 * How invoices are numbered and what is printed at the top of each one.
 * The GST/HST number belongs on every invoice over $30 by the CRA's rules,
 * which is why it has its own field rather than living in the address.
 */
export function InvoicingSettingsCard({
  organizationId,
  canEdit,
}: {
  organizationId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const settings = useQuery(invoiceSettingsQuery(organizationId));
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    prefix: '',
    nextNumber: '',
    businessNumber: '',
    address: '',
    phone: '',
    email: '',
    footer: '',
    defaultTermsDays: '',
  });
  const [error, setError] = useState<string | null>(null);

  const begin = () => {
    const current = settings.data;
    if (current === undefined) return;
    setForm({
      prefix: current.prefix,
      nextNumber: String(current.nextNumber),
      businessNumber: current.businessNumber ?? '',
      address: current.address ?? '',
      phone: current.phone ?? '',
      email: current.email ?? '',
      footer: current.footer ?? '',
      defaultTermsDays: String(current.defaultTermsDays),
    });
    setError(null);
    setEditing(true);
  };

  const save = useMutation({
    mutationFn: () =>
      updateInvoiceSettings(organizationId, {
        prefix: form.prefix.trim(),
        nextNumber: Number(form.nextNumber),
        businessNumber: form.businessNumber.trim() === '' ? null : form.businessNumber.trim(),
        address: form.address.trim() === '' ? null : form.address.trim(),
        phone: form.phone.trim() === '' ? null : form.phone.trim(),
        email: form.email.trim() === '' ? null : form.email.trim(),
        footer: form.footer.trim() === '' ? null : form.footer.trim(),
        defaultTermsDays: Number(form.defaultTermsDays),
      }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all(organizationId) });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The settings were not saved.'),
  });

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((before) => ({ ...before, [key]: event.target.value }));

  const current = settings.data;
  const valid =
    Number(form.nextNumber) >= 1 &&
    Number.isInteger(Number(form.nextNumber)) &&
    Number(form.defaultTermsDays) >= 0 &&
    Number(form.defaultTermsDays) <= 365;

  return (
    <Card>
      <CardHeader
        title="Invoicing"
        description={
          current === undefined
            ? 'Reading…'
            : `The next invoice will be ${current.nextInvoiceNumber}, due ${current.defaultTermsDays === 0 ? 'on receipt' : `${String(current.defaultTermsDays)} days after its date`}.${current.businessNumber === null ? ' No GST/HST number is printed yet; the CRA requires it on invoices over $30.' : ''}`
        }
      />
      <CardBody className="flex flex-col gap-4">
        {error === null ? null : <Alert tone="danger">{error}</Alert>}
        {settings.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : !editing ? (
          <>
            <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <Item label="GST/HST number" value={current?.businessNumber ?? 'Not set'} />
              <Item
                label="Phone · email"
                value={[current?.phone, current?.email].filter(Boolean).join(' · ') || 'Not set'}
              />
              <Item label="Address" value={current?.address ?? 'Not set'} />
              <Item label="Footer" value={current?.footer ?? 'None'} />
            </dl>
            {canEdit ? (
              <div>
                <Button variant="secondary" onClick={begin}>
                  Change invoice details
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Number prefix" hint="Before the number: INV-0001.">
                {({ inputProps }) => (
                  <Input {...inputProps} value={form.prefix} onChange={set('prefix')} />
                )}
              </Field>
              <Field label="Next number" hint="Numbers are never reused, only moved forward.">
                {({ inputProps }) => (
                  <Input
                    {...inputProps}
                    inputMode="numeric"
                    value={form.nextNumber}
                    onChange={set('nextNumber')}
                  />
                )}
              </Field>
              <Field label="GST/HST number" hint="Printed on every invoice. 123456789 RT0001.">
                {({ inputProps }) => (
                  <Input
                    {...inputProps}
                    value={form.businessNumber}
                    onChange={set('businessNumber')}
                  />
                )}
              </Field>
              <Field label="Default terms (days)" hint="0 means due on receipt.">
                {({ inputProps }) => (
                  <Input
                    {...inputProps}
                    inputMode="numeric"
                    value={form.defaultTermsDays}
                    onChange={set('defaultTermsDays')}
                  />
                )}
              </Field>
              <Field label="Phone">
                {({ inputProps }) => (
                  <Input {...inputProps} value={form.phone} onChange={set('phone')} />
                )}
              </Field>
              <Field label="Email">
                {({ inputProps }) => (
                  <Input {...inputProps} type="email" value={form.email} onChange={set('email')} />
                )}
              </Field>
              <div className="sm:col-span-2">
                <Field label="Address" hint="As it should print, line breaks kept.">
                  {({ inputProps }) => (
                    <textarea
                      {...inputProps}
                      rows={3}
                      value={form.address}
                      onChange={set('address')}
                      className="w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm"
                    />
                  )}
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Footer" hint="Payment instructions, e-transfer address, thanks.">
                  {({ inputProps }) => (
                    <textarea
                      {...inputProps}
                      rows={3}
                      value={form.footer}
                      onChange={set('footer')}
                      className="w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm"
                    />
                  )}
                </Field>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => save.mutate()} isLoading={save.isPending} disabled={!valid}>
                Save
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-content-muted">{label}</dt>
      <dd className="whitespace-pre-line">{value}</dd>
    </div>
  );
}
